/**
 * Renderer - Canvas 2D Rendering System
 * 
 * Handles all canvas drawing operations with support for interpolation
 * between physics frames for smooth rendering.
 */

import { PhysicsWorld, PhysicsBody } from './PhysicsWorld';

/** Colors matching the game's visual style */
export const Colors = {
    background: '#1a1a1a',
    box: '#8B4513',         // Brown for boxes
    bag: '#C19A6B',         // Tan for bags
    triangle: '#CD853F',    // Peru for triangles
    wall: '#555555',        // Gray for container walls
    conveyor: '#333333',    // Dark gray for conveyor
    highlight: '#FFFF00',   // Yellow for held package highlight
    fillLine: '#666666',    // Gray for fill line indicator
};

/** Render state for interpolation */
interface RenderState {
    x: number;
    y: number;
    angle: number;
}

/**
 * Renderer class - manages canvas drawing
 */
export class Renderer {
    private canvas: HTMLCanvasElement;
    private ctx: CanvasRenderingContext2D;
    private width = 0;
    private height = 0;
    private dpr = 1;

    // Previous frame positions for interpolation
    private prevStates: Map<number, RenderState> = new Map();
    private currStates: Map<number, RenderState> = new Map();

    constructor(canvas: HTMLCanvasElement) {
        this.canvas = canvas;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            throw new Error('Could not get 2D rendering context');
        }
        this.ctx = ctx;
        this.resize();
    }

    /** Handle window resize */
    resize(): void {
        this.dpr = window.devicePixelRatio || 1;
        this.width = window.innerWidth;
        this.height = window.innerHeight;

        // Set canvas size accounting for device pixel ratio
        this.canvas.width = this.width * this.dpr;
        this.canvas.height = this.height * this.dpr;
        this.canvas.style.width = `${this.width}px`;
        this.canvas.style.height = `${this.height}px`;

        // Scale context for DPR
        this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    }

    /** Get canvas dimensions */
    getDimensions(): { width: number; height: number } {
        return { width: this.width, height: this.height };
    }

    /** Store current physics state for next frame interpolation */
    captureState(physics: PhysicsWorld): void {
        // Move current to previous
        this.prevStates = this.currStates;
        this.currStates = new Map();

        // Capture current positions
        for (const body of physics.getAllBodies()) {
            const pos = physics.getPosition(body.handle);
            const angle = physics.getRotation(body.handle);
            if (pos !== null && angle !== null) {
                this.currStates.set(body.handle, { x: pos.x, y: pos.y, angle });
            }
        }
    }

    /** Get interpolated position for smooth rendering */
    private getInterpolatedState(handle: number, alpha: number): RenderState | null {
        const curr = this.currStates.get(handle);
        const prev = this.prevStates.get(handle);

        if (!curr) return null;
        if (!prev) return curr;

        // Lerp position
        const x = prev.x + (curr.x - prev.x) * alpha;
        const y = prev.y + (curr.y - prev.y) * alpha;

        // Lerp angle (handle wrap-around)
        let angleDiff = curr.angle - prev.angle;
        if (angleDiff > Math.PI) angleDiff -= Math.PI * 2;
        if (angleDiff < -Math.PI) angleDiff += Math.PI * 2;
        const angle = prev.angle + angleDiff * alpha;

        return { x, y, angle };
    }

    /** Clear the canvas */
    clear(): void {
        this.ctx.fillStyle = Colors.background;
        this.ctx.fillRect(0, 0, this.width, this.height);
    }

    /** Render all physics bodies */
    renderBodies(physics: PhysicsWorld, alpha: number, heldBodyHandle?: number): void {
        for (const body of physics.getAllBodies()) {
            const state = this.getInterpolatedState(body.handle, alpha);
            if (!state) continue;

            const isHeld = body.handle === heldBodyHandle;
            this.renderBody(body, state, isHeld);
        }
    }

    /** Render a single body */
    private renderBody(body: PhysicsBody, state: RenderState, isHeld: boolean): void {
        const { userData } = body;
        const { x, y, angle } = state;

        this.ctx.save();
        this.ctx.translate(x, y);
        this.ctx.rotate(angle);

        // Draw based on shape type
        switch (userData.shapeType) {
            case 'box':
                this.renderBox(userData.width ?? 50, userData.height ?? 50, isHeld);
                break;
            case 'bag':
                this.renderBag(
                    userData.width ?? 50,
                    userData.height ?? 50,
                    userData.chamferRadius ?? 10,
                    isHeld
                );
                break;
            case 'triangle':
                this.renderTriangle(userData.radius ?? 30, isHeld);
                break;
        }

        this.ctx.restore();
    }

    /** Render a box */
    private renderBox(width: number, height: number, isHeld: boolean): void {
        const halfW = width / 2;
        const halfH = height / 2;

        // Highlight if held
        if (isHeld) {
            this.ctx.strokeStyle = Colors.highlight;
            this.ctx.lineWidth = 3;
            this.ctx.strokeRect(-halfW - 2, -halfH - 2, width + 4, height + 4);
        }

        // Fill
        this.ctx.fillStyle = Colors.box;
        this.ctx.fillRect(-halfW, -halfH, width, height);

        // Border
        this.ctx.strokeStyle = '#5D3A1A';
        this.ctx.lineWidth = 1;
        this.ctx.strokeRect(-halfW, -halfH, width, height);
    }

    /** Render a bag (rounded rectangle) */
    private renderBag(width: number, height: number, radius: number, isHeld: boolean): void {
        const halfW = width / 2;
        const halfH = height / 2;
        const r = Math.min(radius, halfW, halfH);

        // Highlight if held
        if (isHeld) {
            this.ctx.strokeStyle = Colors.highlight;
            this.ctx.lineWidth = 3;
            this.roundRect(-halfW - 2, -halfH - 2, width + 4, height + 4, r + 2);
            this.ctx.stroke();
        }

        // Fill
        this.ctx.fillStyle = Colors.bag;
        this.ctx.beginPath();
        this.roundRect(-halfW, -halfH, width, height, r);
        this.ctx.fill();

        // Border
        this.ctx.strokeStyle = '#8B7355';
        this.ctx.lineWidth = 1;
        this.ctx.beginPath();
        this.roundRect(-halfW, -halfH, width, height, r);
        this.ctx.stroke();

        // X decoration
        this.ctx.strokeStyle = '#6B4423';
        this.ctx.lineWidth = 2;
        const inset = r * 0.8;
        this.ctx.beginPath();
        this.ctx.moveTo(-halfW + inset, -halfH + inset);
        this.ctx.lineTo(halfW - inset, halfH - inset);
        this.ctx.moveTo(halfW - inset, -halfH + inset);
        this.ctx.lineTo(-halfW + inset, halfH - inset);
        this.ctx.stroke();
    }

    /** Render a triangle */
    private renderTriangle(radius: number, isHeld: boolean): void {
        const points = [
            { x: 0, y: -radius },                                          // Top
            { x: radius * Math.cos(Math.PI / 6), y: radius * 0.5 },       // Bottom right
            { x: -radius * Math.cos(Math.PI / 6), y: radius * 0.5 }       // Bottom left
        ];

        // Highlight if held
        if (isHeld) {
            this.ctx.strokeStyle = Colors.highlight;
            this.ctx.lineWidth = 3;
            this.ctx.beginPath();
            const first = points[0];
            if (first) {
                this.ctx.moveTo(first.x, first.y);
                for (let i = 1; i < points.length; i++) {
                    const p = points[i];
                    if (p) this.ctx.lineTo(p.x, p.y);
                }
            }
            this.ctx.closePath();
            this.ctx.stroke();
        }

        // Fill
        this.ctx.fillStyle = Colors.triangle;
        this.ctx.beginPath();
        const first = points[0];
        if (first) {
            this.ctx.moveTo(first.x, first.y);
            for (let i = 1; i < points.length; i++) {
                const p = points[i];
                if (p) this.ctx.lineTo(p.x, p.y);
            }
        }
        this.ctx.closePath();
        this.ctx.fill();

        // Border
        this.ctx.strokeStyle = '#8B5A2B';
        this.ctx.lineWidth = 1;
        this.ctx.stroke();
    }

    /** Helper to draw rounded rectangle path */
    private roundRect(x: number, y: number, w: number, h: number, r: number): void {
        this.ctx.beginPath();
        this.ctx.moveTo(x + r, y);
        this.ctx.lineTo(x + w - r, y);
        this.ctx.quadraticCurveTo(x + w, y, x + w, y + r);
        this.ctx.lineTo(x + w, y + h - r);
        this.ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
        this.ctx.lineTo(x + r, y + h);
        this.ctx.quadraticCurveTo(x, y + h, x, y + h - r);
        this.ctx.lineTo(x, y + r);
        this.ctx.quadraticCurveTo(x, y, x + r, y);
        this.ctx.closePath();
    }

    /** Render container walls */
    renderContainer(
        floorY: number,
        leftWallX: number,
        rightWallX: number,
        wallThickness: number,
        containerTop: number
    ): void {
        this.ctx.fillStyle = Colors.wall;

        // Floor
        this.ctx.fillRect(leftWallX, floorY, rightWallX - leftWallX, wallThickness);

        // Left wall
        this.ctx.fillRect(leftWallX - wallThickness, containerTop, wallThickness, floorY - containerTop + wallThickness);

        // Right wall
        this.ctx.fillRect(rightWallX, containerTop, wallThickness, floorY - containerTop + wallThickness);
    }

    /** Render conveyor belt */
    renderConveyor(x: number, y: number, width: number, height: number, offset: number, chevronSpacing?: number, chevronSize?: number): void {
        this.ctx.fillStyle = Colors.conveyor;
        this.ctx.fillRect(x, y, width, height);

        // Animated chevron pattern - use provided values or defaults
        const spacing = chevronSpacing ?? 20;
        const size = chevronSize ?? 8;
        
        this.ctx.strokeStyle = '#444444';
        this.ctx.lineWidth = 2;
        const chevronCount = Math.ceil(width / spacing) + 2;
        
        this.ctx.save();
        this.ctx.beginPath();
        this.ctx.rect(x, y, width, height);
        this.ctx.clip();

        for (let i = 0; i < chevronCount; i++) {
            const cx = x + (i * spacing) + (offset % spacing);
            this.ctx.beginPath();
            this.ctx.moveTo(cx, y);
            this.ctx.lineTo(cx + size, y + height / 2);
            this.ctx.lineTo(cx, y + height);
            this.ctx.stroke();
        }
        this.ctx.restore();
    }

    /** Render FPS counter */
    renderFps(fps: number): void {
        this.ctx.fillStyle = '#00FF00';
        this.ctx.font = '14px monospace';
        this.ctx.fillText(`FPS: ${fps}`, 10, 20);
    }

    /** Render spring line from grab point to target */
    renderSpringLine(line: { fromX: number; fromY: number; toX: number; toY: number }): void {
        this.ctx.save();
        
        // Draw dashed line
        this.ctx.setLineDash([6, 4]);
        this.ctx.strokeStyle = Colors.highlight;
        this.ctx.lineWidth = 2;
        this.ctx.globalAlpha = 0.7;
        
        this.ctx.beginPath();
        this.ctx.moveTo(line.fromX, line.fromY);
        this.ctx.lineTo(line.toX, line.toY);
        this.ctx.stroke();
        
        // Draw target circle
        this.ctx.setLineDash([]);
        this.ctx.beginPath();
        this.ctx.arc(line.toX, line.toY, 6, 0, Math.PI * 2);
        this.ctx.stroke();
        
        // Draw grab point
        this.ctx.fillStyle = Colors.highlight;
        this.ctx.beginPath();
        this.ctx.arc(line.fromX, line.fromY, 4, 0, Math.PI * 2);
        this.ctx.fill();
        
        this.ctx.restore();
    }

    /** Render debug info */
    renderDebugInfo(info: string[]): void {
        this.ctx.fillStyle = '#00FF00';
        this.ctx.font = '12px monospace';
        info.forEach((line, i) => {
            this.ctx.fillText(line, 10, 40 + i * 16);
        });
    }
}
